import { SortingState, OnChangeFn } from '@tanstack/react-table'

/**
 * Creates a set of operations for manipulating a SortingState within a state object
 * This is specifically designed to work with TanStack Table's sorting state
 *
 * @param key - The key of the state object that contains a SortingState
 * @returns A function that takes state and setState function and returns an object with sorting operations
 *
 * @example
 * // Types are inferred automatically
 * const sortingOps = createSortingOperationsFor('sorting')(state, setState)
 */
export function createSortingOperationsFor<K extends string>(key: K) {
  return <S extends Record<K, SortingState>>(
    state: S,
    setState: (updater: (prev: S) => S) => void,
  ) => {
    return {
      /**
       * The current value of the SortingState
       */
      value: state[key],

      /**
       * Sets the sorting state
       * Compatible with TanStack Table's OnChangeFn<SortingState> interface
       */
      set: ((sortingState) => {
        setState((prev) => ({
          ...prev,
          [key]:
            typeof sortingState === 'function'
              ? sortingState(prev[key])
              : sortingState,
        }))
      }) as OnChangeFn<SortingState>,
    }
  }
}
